import React, { useState } from 'react'
import {
	Typography,
	Row,
	Col,
	Switch,
	Button,
	Modal,
	Table,
	Tag,
	Space,
	DatePicker,
	message,
} from 'antd'
import { LaptopOutlined, ReloadOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { httpGet } from '../../common/http.service'

const { Title, Text, Paragraph } = Typography

interface DeviceChangeTrackingSubTabProps {
	enableDeviceChangeTracking: boolean
	onToggle: (value: boolean) => void
	saving: boolean
}

interface DeviceChangeRecord {
	id: string
	dateKey?: string | null
	staffId: string
	staffName: string | null
	action: 'checkin' | 'checkout'
	fromDeviceId: string | null
	fromDeviceName: string | null
	toDeviceId: string
	toDeviceName: string | null
	fromDeviceStaffIds: string[]
	toDeviceStaffIds: string[]
	recordedAt: string | null
}

const DeviceChangeTrackingSubTab: React.FC<DeviceChangeTrackingSubTabProps> = ({
	enableDeviceChangeTracking,
	onToggle,
	saving,
}) => {
	const [recordOpen, setRecordOpen] = useState(false)
	const [recordLoading, setRecordLoading] = useState(false)
	const [records, setRecords] = useState<DeviceChangeRecord[]>([])
	const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs())

	const loadDeviceChangeRecords = async (dateVal: Dayjs | null = selectedDate) => {
		try {
			setRecordLoading(true)
			const params: Record<string, any> = { limit: 300 }
			if (dateVal) {
				params.date = dateVal.format('YYYY-MM-DD')
			}
			const response: any = await httpGet(
				'/api/v1/admin/fraud-detection/device-change-records',
				params,
			)
			const rows = Array.isArray(response?.data) ? response.data : []
			setRecords(rows)
		} catch (error) {
			console.error('Failed to load device change records:', error)
			message.error('Failed to load device change records')
		} finally {
			setRecordLoading(false)
		}
	}

	const columns = [
		{
			title: 'Date',
			key: 'dateKey',
			width: 115,
			render: (_: any, r: DeviceChangeRecord) =>
				r.dateKey || (r.recordedAt ? dayjs(r.recordedAt).format('YYYY-MM-DD') : '—'),
		},
		{
			title: 'Time',
			dataIndex: 'recordedAt',
			width: 100,
			render: (v: string | null) => (v ? dayjs(v).format('HH:mm:ss') : '—'),
		},
		{
			title: 'Staff ID',
			dataIndex: 'staffId',
			width: 110,
		},
		{
			title: 'Staff Name',
			dataIndex: 'staffName',
			width: 180,
			render: (v: string | null) => v || '—',
		},
		{
			title: 'Action',
			dataIndex: 'action',
			width: 100,
			render: (v: string) => <Tag color={v === 'checkout' ? 'gold' : 'blue'}>{v}</Tag>,
		},
		{
			title: 'From Device',
			key: 'fromDevice',
			render: (_: any, r: DeviceChangeRecord) =>
				r.fromDeviceId ? `${r.fromDeviceName || 'Unknown'} (${r.fromDeviceId})` : '—',
		},
		{
			title: 'To Device',
			key: 'toDevice',
			render: (_: any, r: DeviceChangeRecord) =>
				r.toDeviceId ? `${r.toDeviceName || 'Unknown'} (${r.toDeviceId})` : '—',
		},
		{
			title: 'Other Staff on Same Device (Same Day)',
			key: 'mappedStaff',
			render: (_: any, r: DeviceChangeRecord) => {
				const otherFrom = (r.fromDeviceStaffIds || []).filter((id) => id && id !== r.staffId)
				const otherTo = (r.toDeviceStaffIds || []).filter((id) => id && id !== r.staffId)
				if (otherFrom.length === 0 && otherTo.length === 0) {
					return <Tag color='green'>No other staff</Tag>
				}
				return (
					<div>
						{otherFrom.length > 0 && (
							<div style={{ marginBottom: 4 }}>
								<Text strong>From device shared with: </Text>
								{otherFrom.map((id) => (
									<Tag color='volcano' key={`from-${id}`}>
										{id}
									</Tag>
								))}
							</div>
						)}
						{otherTo.length > 0 && (
							<div>
								<Text strong>To device shared with: </Text>
								{otherTo.map((id) => (
									<Tag color='volcano' key={`to-${id}`}>
										{id}
									</Tag>
								))}
							</div>
						)}
					</div>
				)
			},
		},
	]

	return (
		<div>
			<Title level={4} style={{ marginBottom: 16 }}>
				<LaptopOutlined style={{ marginRight: 8 }} />
				Device Change Tracking Settings
			</Title>
			<Paragraph type='secondary' style={{ marginBottom: 24 }}>
				Monitor and record instances where staff members change their check-in/out devices by day.
			</Paragraph>

			<Row align='middle' justify='space-between' style={{ padding: '12px 0' }}>
				<Col flex='1'>
					<Title level={5} style={{ margin: 0 }}>
						Track Device Changes by Staff (Daily)
					</Title>
					<Text type='secondary'>
						When enabled, the system records per day whenever a staff account checks in/out
						using a different device, and highlights if any other staff member shared the
						same device on that day.
					</Text>
					<div style={{ marginTop: 12 }}>
						<Button
							icon={<LaptopOutlined />}
							onClick={async () => {
								setRecordOpen(true)
								await loadDeviceChangeRecords(selectedDate)
							}}
						>
							View device change record
						</Button>
					</div>
				</Col>
				<Col>
					<Switch
						checked={enableDeviceChangeTracking}
						onChange={onToggle}
						loading={saving}
						checkedChildren='Enabled'
						unCheckedChildren='Disabled'
					/>
				</Col>
			</Row>

			<Modal
				title={
					<Space wrap>
						<LaptopOutlined />
						<span>Device Change Tracking Records</span>
						<DatePicker
							size='small'
							allowClear
							placeholder='All Days'
							value={selectedDate}
							onChange={async (date) => {
								setSelectedDate(date)
								await loadDeviceChangeRecords(date)
							}}
						/>
						<Button
							size='small'
							onClick={async () => {
								setSelectedDate(null)
								await loadDeviceChangeRecords(null)
							}}
						>
							All Days
						</Button>
						<Button
							size='small'
							icon={<ReloadOutlined />}
							loading={recordLoading}
							onClick={() => loadDeviceChangeRecords(selectedDate)}
						>
							Refresh
						</Button>
					</Space>
				}
				open={recordOpen}
				onCancel={() => setRecordOpen(false)}
				footer={null}
				width={1150}
			>
				<Table<DeviceChangeRecord>
					rowKey={(r) => r.id}
					loading={recordLoading}
					dataSource={records}
					scroll={{ x: 'max-content' }}
					pagination={{
						pageSize: 10,
						showSizeChanger: true,
						showTotal: (total) => `Total ${total} records`,
					}}
					columns={columns}
				/>
			</Modal>
		</div>
	)
}

export default DeviceChangeTrackingSubTab
